import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './DesktopPostMediaManager.css';
import useLanguage from '../../../../hooks/useLanguage';
import { useDesktopAlert } from '../alerts/DesktopAlertContext';
import iconAi from '../../../../assets/images/icons/icon-ai.svg';
import {
  SUPPORTED_MEDIA_ACCEPT,
  SUPPORTED_MEDIA_LABEL,
  isSupportedMediaFile
} from '../../../Mobile/mediaUploadConfig';

const UploadIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="17 8 12 3 7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const GalleryIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
    <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrashIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="10" y1="11" x2="10" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="14" y1="11" x2="14" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const defaultGetImageClassNames = (baseClass, loadingProps) =>
  [
    baseClass,
    loadingProps ? 'mobile-image-with-loading' : '',
    loadingProps?.className || ''
  ].filter(Boolean).join(' ');

const DeleteConfirmModal = ({ imageIndex, onConfirm, onCancel }) => {
  const { t } = useLanguage();
  const imageNumber = imageIndex !== null ? imageIndex + 1 : null;
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  }, [onCancel]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="delete-confirm-modal-overlay" onClick={handleOverlayClick}>
      <div className="delete-confirm-modal">
        <div className="delete-confirm-header">
          <div className="delete-confirm-icon">
            <TrashIcon size={28} />
          </div>
          <h3 className="delete-confirm-title">{t('postReview.postEdit.delete.title')}</h3>
        </div>
        <div className="delete-confirm-content">
          <p className="delete-confirm-message">
            {imageNumber !== null
              ? t('postReview.postEdit.delete.messageWithIndex', { index: imageNumber })
              : t('postReview.postEdit.delete.message')}
            <br />
            {t('postReview.postEdit.delete.warning')}
          </p>
        </div>
        <div className="delete-confirm-actions">
          <button className="delete-confirm-btn cancel" onClick={onCancel}>
            {t('postReview.postEdit.delete.cancel')}
          </button>
          <button className="delete-confirm-btn confirm" onClick={onConfirm}>
            <TrashIcon size={16} />
            {t('postReview.postEdit.delete.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const DesktopPostMediaManager = ({
  mediaArray = [],
  isUiDisabled = false,
  isGenerating = false,
  onUploadFiles,
  onOpenGenerate,
  onOpenGallery,
  onDelete,
  onReorder,
  onDraggingChange,
  renderOverlay,
  getImageLoadingProps,
  getImageClassNames = defaultGetImageClassNames,
  imageLoadingScope = 'desktop-post-media'
}) => {
  const { t } = useLanguage();
  const { notify } = useDesktopAlert();
  const gridRef = useRef(null);
  const ghostRef = useRef(null);
  const fileInputRef = useRef(null);

  const unsupportedFormatMessage = useMemo(
    () => t('postReview.postEdit.media.unsupportedFormat', { defaultValue: `Formato non supportato. Usa: ${SUPPORTED_MEDIA_LABEL}` }),
    [t]
  );

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [lastValidTarget, setLastValidTarget] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const [pressedImageIndex, setPressedImageIndex] = useState(null);
  const [isOverTrash, setIsOverTrash] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [dragCurrentPos, setDragCurrentPos] = useState({ x: 0, y: 0 });
  const [dragGridBounds, setDragGridBounds] = useState(null);
  const [draggedImageDimensions, setDraggedImageDimensions] = useState({ width: 0, height: 0 });
  const [dragPointerOffset, setDragPointerOffset] = useState({ x: 0, y: 0 });
  const [forceRenderKey, setForceRenderKey] = useState(0);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteImageIndex, setDeleteImageIndex] = useState(null);

  const [videoThumbnails, setVideoThumbnails] = useState({});
  const [thumbnailsLoading, setThumbnailsLoading] = useState({});

  const generatingLabel = useMemo(
    () => t('imageGenerator.status.generating', { defaultValue: 'Generazione...' }),
    [t]
  );

  useEffect(() => {
    onDraggingChange?.(isDragging);
  }, [isDragging, onDraggingChange]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (!isDragging) {
      document.body.classList.remove('loopino-desktop-media-dragging');
      return undefined;
    }

    document.body.classList.add('loopino-desktop-media-dragging');
    return () => {
      document.body.classList.remove('loopino-desktop-media-dragging');
    };
  }, [isDragging]);

  const isVideoFile = useCallback((url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.ogg', '.mkv', '.m4v'];
    const urlLowerCase = url.toLowerCase();
    return videoExtensions.some(ext => urlLowerCase.includes(ext));
  }, []);

  const generateVideoThumbnail = useCallback((videoUrl) => new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    const handleLoadedData = () => {
      try {
        const aspectRatio = video.videoWidth / video.videoHeight;
        canvas.width = 120;
        canvas.height = 120 / aspectRatio;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve(thumbnailDataUrl);
      } catch (error) {
        reject(error);
      } finally {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('error', handleError);
        video.src = '';
        video.load();
      }
    };

    const handleError = () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
      reject(new Error('Failed to load video for thumbnail generation'));
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('error', handleError);

    video.src = videoUrl;
    video.currentTime = 0.1;
  }), []);

  useEffect(() => {
    const generateThumbnails = async () => {
      if (!mediaArray || mediaArray.length === 0) return;

      for (let i = 0; i < mediaArray.length; i++) {
        const mediaItem = mediaArray[i];
        const displaySource = mediaItem?.previewUrl || mediaItem?.url;
        const thumbnailKey = `${mediaItem?.id || displaySource}-${i}`;
        const isVideo = mediaItem?.type === 'file'
          ? !!mediaItem.file && mediaItem.file.type.startsWith('video/')
          : isVideoFile(mediaItem?.url || displaySource);

        if (isVideo && displaySource && !videoThumbnails[thumbnailKey] && !thumbnailsLoading[thumbnailKey]) {
          setThumbnailsLoading(prev => ({ ...prev, [thumbnailKey]: true }));
          try {
            const thumbnail = await generateVideoThumbnail(displaySource);
            setVideoThumbnails(prev => ({ ...prev, [thumbnailKey]: thumbnail }));
          } catch (error) {
            console.error('[DesktopPostMediaManager] Error generating video thumbnail:', error);
          } finally {
            setThumbnailsLoading(prev => ({ ...prev, [thumbnailKey]: false }));
          }
        }
      }
    };

    generateThumbnails();
  }, [generateVideoThumbnail, isVideoFile, mediaArray, thumbnailsLoading, videoThumbnails]);

  const resetDrag = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setLastValidTarget(null);
    setDragStartPos({ x: 0, y: 0 });
    setDragCurrentPos({ x: 0, y: 0 });
    setDragGridBounds(null);
    setDraggedImageDimensions({ width: 0, height: 0 });
    setDragPointerOffset({ x: 0, y: 0 });
    setPressedImageIndex(null);
    setIsOverTrash(false);
  }, []);

  const startDragMode = useCallback((index, startX, startY) => {
    const gridElement = gridRef.current;
    if (gridElement) {
      setDragGridBounds(gridElement.getBoundingClientRect());
      const imageContainer = gridElement.querySelector(`[data-image-index="${index}"]`);
      if (imageContainer) {
        const rect = imageContainer.getBoundingClientRect();
        setDraggedImageDimensions({ width: rect.width, height: rect.height });
        setDragPointerOffset({
          x: Math.max(0, Math.min(rect.width, startX - rect.left)),
          y: Math.max(0, Math.min(rect.height, startY - rect.top))
        });
      } else {
        setDraggedImageDimensions({ width: 240, height: 240 });
        setDragPointerOffset({ x: 120, y: 120 });
      }
    } else {
      setDraggedImageDimensions({ width: 240, height: 240 });
      setDragPointerOffset({ x: 120, y: 120 });
    }

    setDraggedIndex(index);
    setDragStartPos({ x: startX, y: startY });
    setDragCurrentPos({ x: startX, y: startY });
    setDragOverIndex(null);
    setLastValidTarget(null);
    isDraggingRef.current = true;
    setIsDragging(true);
  }, []);

  const handleDragMove = useCallback((clientX, clientY, sourceIndex) => {
    if (!isDraggingRef.current) return;

    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : null;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : null;

    let newX = clientX;
    let newY = clientY;
    if (Number.isFinite(viewportWidth)) {
      newX = Math.max(0, Math.min(viewportWidth, newX));
    }
    if (Number.isFinite(viewportHeight)) {
      newY = Math.max(0, Math.min(viewportHeight, newY));
    }
    setDragCurrentPos({ x: newX, y: newY });

    if (ghostRef.current) ghostRef.current.style.display = 'none';
    const elementBelow = document.elementFromPoint(newX, newY);
    if (ghostRef.current) ghostRef.current.style.display = '';

    const imageItem = elementBelow?.closest('.post-edit-image-item');
    const trashArea = elementBelow?.closest('[data-delete-zone="true"]');
    const isTrashHit = Boolean(trashArea && trashArea.getAttribute('data-delete-zone') === 'true');

    if (isTrashHit) {
      setIsOverTrash(true);
      setDragOverIndex(null);
      return;
    }

    setIsOverTrash(false);

    if (
      imageItem
      && !imageItem.classList.contains('post-edit-add-image-item')
      && !imageItem.classList.contains('post-edit-generating-item')
    ) {
      const targetIndex = parseInt(imageItem.dataset.index, 10);
      if (!Number.isNaN(targetIndex) && targetIndex !== sourceIndex) {
        setDragOverIndex(targetIndex);
        setLastValidTarget(targetIndex);
      }
      return;
    }
  }, []);

  const handleDragComplete = useCallback((finalTargetIndex = null, droppedOnDelete = false, sourceIndex = null) => {
    const currentDraggedIndex = sourceIndex !== null ? sourceIndex : draggedIndex;
    const shouldDelete = Boolean(droppedOnDelete || isOverTrash);

    if (currentDraggedIndex !== null && shouldDelete) {
      setDeleteImageIndex(currentDraggedIndex);
      setShowDeleteConfirm(true);
      resetDrag();
      return;
    }

    const targetIndex = finalTargetIndex !== null
      ? finalTargetIndex
      : (dragOverIndex !== null ? dragOverIndex : lastValidTarget);

    if (
      typeof onReorder === 'function' &&
      currentDraggedIndex !== null &&
      targetIndex !== null &&
      currentDraggedIndex !== targetIndex
    ) {
      onReorder(currentDraggedIndex, targetIndex);
      setForceRenderKey((prev) => prev + 1);
    }

    resetDrag();
  }, [dragOverIndex, draggedIndex, isOverTrash, lastValidTarget, onReorder, resetDrag]);

  const handleImageMouseDown = useCallback((e, index) => {
    if (isUiDisabled) return;
    if (e.target.closest('button')) return;

    e.preventDefault();
    setPressedImageIndex(index);

    const startX = e.clientX;
    const startY = e.clientY;
    let hasMoved = false;
    let hasStartedDrag = false;

    startDragMode(index, startX, startY);
    hasStartedDrag = true;

    const handleMouseMove = (moveEvent) => {
      const deltaX = Math.abs(moveEvent.clientX - startX);
      const deltaY = Math.abs(moveEvent.clientY - startY);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance > 0) hasMoved = true;

      if (hasStartedDrag && isDraggingRef.current) {
        handleDragMove(moveEvent.clientX, moveEvent.clientY, index);
      }
    };

    const handleMouseUp = (upEvent) => {
      if (!hasMoved) {
        handleDragComplete(null, false, index);
      } else if (hasStartedDrag) {
        if (ghostRef.current) ghostRef.current.style.display = 'none';
        const elementBelow = document.elementFromPoint(upEvent.clientX, upEvent.clientY);
        if (ghostRef.current) ghostRef.current.style.display = '';

        const imageItem = elementBelow?.closest('.post-edit-image-item');
        const deleteZone = elementBelow?.closest('[data-delete-zone="true"]');

        let finalTargetIndex = null;
        let droppedOnDelete = Boolean(deleteZone);

        if (
          !droppedOnDelete
          && imageItem
          && !imageItem.classList.contains('post-edit-add-image-item')
          && !imageItem.classList.contains('post-edit-generating-item')
        ) {
          const targetIndex = parseInt(imageItem.dataset.index, 10);
          if (!Number.isNaN(targetIndex) && targetIndex !== index) {
            finalTargetIndex = targetIndex;
          }
        }

        if (finalTargetIndex === null && !droppedOnDelete) {
          if (isOverTrash) {
            droppedOnDelete = true;
          } else if (dragOverIndex !== null && dragOverIndex !== index) {
            finalTargetIndex = dragOverIndex;
          }
        }

        handleDragComplete(finalTargetIndex, droppedOnDelete, index);
      }

      setPressedImageIndex(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [dragOverIndex, handleDragComplete, handleDragMove, isOverTrash, isUiDisabled, startDragMode]);

  const handleImageTouchStart = useCallback((e, index) => {
    if (isUiDisabled) return;
    if (e.target.closest('button')) return;

    e.preventDefault();
    setPressedImageIndex(index);

    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    let hasMoved = false;
    let hasStartedDrag = false;

    startDragMode(index, startX, startY);
    hasStartedDrag = true;

    const handleTouchMove = (moveEvent) => {
      if (!moveEvent.touches[0]) return;
      const tPoint = moveEvent.touches[0];
      const deltaX = Math.abs(tPoint.clientX - startX);
      const deltaY = Math.abs(tPoint.clientY - startY);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance > 0) hasMoved = true;

      if (hasStartedDrag && isDraggingRef.current) {
        moveEvent.preventDefault();
        handleDragMove(tPoint.clientX, tPoint.clientY, index);
      }
    };

    const handleTouchEnd = (endEvent) => {
      if (!hasMoved) {
        handleDragComplete(null, false, index);
      } else if (hasStartedDrag) {
        const changedTouch = endEvent.changedTouches && endEvent.changedTouches[0];
        const clientX = changedTouch?.clientX ?? dragCurrentPos.x;
        const clientY = changedTouch?.clientY ?? dragCurrentPos.y;

        if (ghostRef.current) ghostRef.current.style.display = 'none';
        const elementBelow = document.elementFromPoint(clientX, clientY);
        if (ghostRef.current) ghostRef.current.style.display = '';

        const imageItem = elementBelow?.closest('.post-edit-image-item');
        const deleteZone = elementBelow?.closest('[data-delete-zone="true"]');

        let finalTargetIndex = null;
        let droppedOnDelete = Boolean(deleteZone);

        if (
          !droppedOnDelete
          && imageItem
          && !imageItem.classList.contains('post-edit-add-image-item')
          && !imageItem.classList.contains('post-edit-generating-item')
        ) {
          const targetIndex = parseInt(imageItem.dataset.index, 10);
          if (!Number.isNaN(targetIndex) && targetIndex !== index) {
            finalTargetIndex = targetIndex;
          }
        }

        if (finalTargetIndex === null && !droppedOnDelete) {
          if (isOverTrash) {
            droppedOnDelete = true;
          } else if (dragOverIndex !== null && dragOverIndex !== index) {
            finalTargetIndex = dragOverIndex;
          }
        }

        handleDragComplete(finalTargetIndex, droppedOnDelete, index);
      }

      setPressedImageIndex(null);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
  }, [dragCurrentPos.x, dragCurrentPos.y, dragOverIndex, handleDragComplete, handleDragMove, isOverTrash, isUiDisabled, startDragMode]);

  const handleUploadButtonClick = useCallback(() => {
    if (isUiDisabled) return;
    fileInputRef.current?.click();
  }, [isUiDisabled]);

  const handleFileInputChange = useCallback((event) => {
    if (isUiDisabled) return;
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const validFiles = [];
    let hasInvalidFile = false;

    Array.from(files).forEach((file) => {
      if (typeof File !== 'undefined' && !(file instanceof File)) {
        hasInvalidFile = true;
        return;
      }

      if (!isSupportedMediaFile(file)) {
        hasInvalidFile = true;
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      onUploadFiles?.(validFiles);
    }

    if (hasInvalidFile && notify) {
      notify({ variant: 'error', title: unsupportedFormatMessage });
    }

    event.target.value = '';
  }, [isUiDisabled, notify, onUploadFiles, unsupportedFormatMessage]);

  const handleConfirmDelete = useCallback(() => {
    if (deleteImageIndex !== null) {
      onDelete?.(deleteImageIndex);
    }
    setShowDeleteConfirm(false);
    setDeleteImageIndex(null);
  }, [deleteImageIndex, onDelete]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setDeleteImageIndex(null);
  }, []);

  const renderMediaContent = useCallback((mediaItem, index) => {
    const sourceUrl = mediaItem?.previewUrl || mediaItem?.url;
    const isVideo = mediaItem?.type === 'file'
      ? !!mediaItem.file && mediaItem.file.type.startsWith('video/')
      : isVideoFile(sourceUrl);

    const thumbnailKey = `${mediaItem?.id || sourceUrl}-${index}`;
    const isLoading = thumbnailsLoading[thumbnailKey];
    const thumbnail = videoThumbnails[thumbnailKey];
    const blurScopeBase = `${imageLoadingScope}-media-${index}`;
    const imageLoadingProps = (!isVideo && getImageLoadingProps)
      ? getImageLoadingProps(sourceUrl, blurScopeBase)
      : null;
    const videoThumbnailLoadingProps = (isVideo && thumbnail && getImageLoadingProps)
      ? getImageLoadingProps(thumbnail, `${blurScopeBase}-video`)
      : null;

    if (isVideo && isLoading) {
      return (
        <div className="post-edit-image-loading">
          <div className="post-edit-thumbnail-spinner"></div>
          <span className="post-edit-loading-text">{t('postReview.postEdit.media.generatingThumbnail')}</span>
        </div>
      );
    }

    if (isVideo && thumbnail) {
      return (
        <>
          <img
            src={thumbnail}
            alt={`Video thumbnail ${index + 1}`}
            className={getImageClassNames('post-edit-image', videoThumbnailLoadingProps)}
            loading="lazy"
            decoding="async"
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            onError={(e) => {
              videoThumbnailLoadingProps?.onError?.(e);
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI1MCIgdmlld0JveD0iMCAwIDIwMCAyNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjUwIiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMSkiLz4KPHJlY3QgeD0iMjAiIHk9IjgwIiB3aWR0aD0iMTYwIiBoZWlnaHQ9IjkwIiByeD0iNSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMykiIHN0cm9rZS13aWR0aD0iMSIvPgo8Y2lyY2xlIGN4PSI2MCIgY3k9IjEwMCIgcj0iOCIgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjMpIi8+CjxwYXRoIGQ9Im0xNjAgMTQwLTQwLTQwLTI1IDI1LTE1LTE1IiBzdHJva2U9InJnYmEoMjU1LCAyNTUsIDI1NSwgMC4zKSIgc3Rva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4=';
            }}
            onLoad={(event) => {
              videoThumbnailLoadingProps?.onLoad?.(event);
            }}
          />
          <div className="post-edit-video-indicator">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polygon points="5,3 19,12 5,21" fill="white"/>
            </svg>
          </div>
        </>
      );
    }

    if (isVideo) {
      return (
        <div className="post-edit-image-loading">
          <div className="post-edit-video-placeholder">🎥</div>
          <span className="post-edit-loading-text">{t('postReview.postEdit.media.videoLabel')}</span>
        </div>
      );
    }

    return (
      <img
        src={sourceUrl}
        alt={t('postReview.postEdit.media.imageAlt', { index: index + 1 })}
        className={getImageClassNames('post-edit-image', imageLoadingProps)}
        loading="lazy"
        decoding="async"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        onError={(e) => {
          imageLoadingProps?.onError?.(e);
          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI1MCIgdmlld0JveD0iMCAwIDIwMCAyNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjUwIiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMSkiLz4KPHJlY3QgeD0iMjAiIHk9IjgwIiB3aWR0aD0iMTYwIiBoZWlnaHQ9IjkwIiByeD0iNSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMykiIHN0cm9rZS13aWR0aD0iMSIvPgo8Y2lyY2xlIGN4PSI2MCIgY3k9IjEwMCIgcj0iOCIgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjMpIi8+CjxwYXRoIGQ9Im0xNjAgMTQwLTQwLTQwLTI1IDI1LTE1LTE1IiBzdHJva2U9InJnYmEoMjU1LCAyNTUsIDI1NSwgMC4zKSIgc3Rva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4=';
        }}
        onLoad={(event) => {
          imageLoadingProps?.onLoad?.(event);
        }}
      />
    );
  }, [getImageClassNames, getImageLoadingProps, imageLoadingScope, isVideoFile, t, thumbnailsLoading, videoThumbnails]);

  return (
    <div className="desktop-post-media-manager">
      <div className="post-edit-section">
        <h2 className="post-edit-section-title">{t('postReview.postEdit.images.title')}</h2>
        <p className="post-edit-section-description">{t('postReview.postEdit.images.helper')}</p>

        <div className="post-edit-images-grid" ref={gridRef} key={`grid-${forceRenderKey}`}>
          {mediaArray.map((mediaItem, index) => {
            const sourceUrl = mediaItem?.previewUrl || mediaItem?.url;
            const isVideo = mediaItem?.type === 'file'
              ? !!mediaItem.file && mediaItem.file.type.startsWith('video/')
              : isVideoFile(sourceUrl);

            return (
              <div
                key={`${mediaItem?.id || index}-${forceRenderKey}`}
                className={`post-edit-image-item ${draggedIndex === index && isDragging ? 'dragging' : ''} ${dragOverIndex === index && isDragging ? 'drag-target' : ''} ${pressedImageIndex === index && !isDragging ? 'pressed' : ''}`}
                data-index={index}
              >
                <div
                  className={`post-edit-image-container ${draggedIndex === index && isDragging ? 'being-dragged' : ''}`}
                  data-image-index={index}
                  onMouseDown={(e) => handleImageMouseDown(e, index)}
                  onTouchStart={(e) => handleImageTouchStart(e, index)}
                >
                  {renderMediaContent(mediaItem, index)}

                  {typeof renderOverlay === 'function'
                    ? renderOverlay({ index, mediaItem, isDragging, isVideo, isUiDisabled })
                    : null}

                  <div className="post-edit-image-number">{index + 1}</div>
                </div>
              </div>
            );
          })}

          {isGenerating && (
            <div
              className="post-edit-image-item post-edit-generating-item"
              data-index={mediaArray.length}
            >
              <div
                className="post-edit-image-container post-edit-generating-container"
                role="status"
                aria-live="polite"
                aria-label={generatingLabel}
              >
                <div className="post-edit-generating-content">
                  <div className="post-edit-generating-ai-badge" aria-hidden="true">
                    <img src={iconAi} alt="" aria-hidden="true" />
                  </div>
                  <div className="post-edit-generating-title">{generatingLabel}</div>
                  <div className="post-edit-generating-subtitle">
                    {t('postReview.postEdit.media.generatingHint', { defaultValue: "L'immagine apparirà qui appena pronta." })}
                  </div>
                </div>
                <div className="post-edit-image-number">{mediaArray.length + 1}</div>
              </div>
            </div>
          )}

          <div
            className={`post-edit-add-image-item ${isDragging ? 'delete-zone' : ''} ${isDragging && isOverTrash ? 'active' : ''}`}
            data-delete-zone="true"
          >
            {!isDragging ? (
              <div className="post-edit-add-image-content">
                <div className="post-edit-add-image-buttons">
                  <button
                    type="button"
                    className="post-edit-add-image-main-btn"
                    onClick={handleUploadButtonClick}
                    aria-label={t('postReview.postEdit.media.uploadAria')}
                    disabled={isUiDisabled}
                  >
                    <span className="post-edit-add-image-btn-icon">
                      <UploadIcon size={18} />
                    </span>
                    <span>{t('postReview.postEdit.media.uploadLabel')}</span>
                  </button>

                  <button
                    type="button"
                    className="post-edit-add-image-main-btn"
                    onClick={() => !isUiDisabled && onOpenGenerate?.()}
                    aria-label={t('postReview.postEdit.media.generateAria')}
                    disabled={isUiDisabled}
                  >
                    <span className="post-edit-add-image-btn-icon">
                      <img src={iconAi} alt="" aria-hidden="true" />
                    </span>
                    <span>{t('postReview.postEdit.media.generateLabel')}</span>
                  </button>

                  <button
                    type="button"
                    className="post-edit-add-image-main-btn"
                    onClick={() => !isUiDisabled && onOpenGallery?.()}
                    aria-label={t('postReview.postEdit.media.galleryAria')}
                    disabled={isUiDisabled}
                  >
                    <span className="post-edit-add-image-btn-icon">
                      <GalleryIcon size={18} />
                    </span>
                    <span>{t('postReview.postEdit.media.galleryLabel')}</span>
                  </button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={SUPPORTED_MEDIA_ACCEPT}
                  multiple
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
              </div>
            ) : (
              <div className="post-edit-delete-zone" data-delete-zone="true">
                <div className="post-edit-delete-icon">
                  <TrashIcon size={32} />
                </div>
                <span className="post-edit-delete-text">
                  {isOverTrash
                    ? t('postReview.postEdit.media.deleteDrop')
                    : t('postReview.postEdit.media.deleteDrag')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {isDragging && draggedIndex !== null && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={ghostRef}
              className="post-edit-drag-ghost"
              data-loopino-desktop-ghost-clone="true"
              data-loopino-desktop-ghost-managed="true"
              style={{
                position: 'fixed',
                zIndex: 20000,
                pointerEvents: 'none',
                left:
                  dragCurrentPos.x
                  - ((dragPointerOffset.x ?? (draggedImageDimensions.width || 240) / 2)),
                top:
                  dragCurrentPos.y
                  - ((dragPointerOffset.y ?? (draggedImageDimensions.height || 240) / 2)),
                width: `${draggedImageDimensions.width || 240}px`,
                height: `${draggedImageDimensions.height || 240}px`,
                opacity: 0.92,
                transform: 'rotate(2deg) scale(1.02)'
              }}
            >
              <div className="post-edit-image-container">
                {(() => {
                  if (!mediaArray || draggedIndex === null || !mediaArray[draggedIndex]) return null;
                  const mediaItem = mediaArray[draggedIndex];
                  const draggedImageUrl = mediaItem?.previewUrl || mediaItem?.url;
                  const thumbnailKey = `${mediaItem?.id || draggedImageUrl}-${draggedIndex}`;
                  const isVideo = mediaItem?.type === 'file'
                    ? !!mediaItem.file && mediaItem.file.type.startsWith('video/')
                    : isVideoFile(mediaItem?.url || draggedImageUrl);
                  const thumbnail = videoThumbnails[thumbnailKey];

                  return (
                    <>
                      <img
                        src={isVideo && thumbnail ? thumbnail : draggedImageUrl}
                        alt={`Dragging ${isVideo ? 'video' : 'image'} ${draggedIndex + 1}`}
                        className="post-edit-image"
                        draggable={false}
                      />
                      {isVideo && thumbnail && (
                        <div className="post-edit-video-indicator ghost-indicator">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polygon points="5,3 19,12 5,21" fill="white"/>
                          </svg>
                        </div>
                      )}
                    </>
                  );
                })()}

                <div className="post-edit-image-number">{draggedIndex + 1}</div>
              </div>
            </div>,
            document.body
          )
        : null}

      {showDeleteConfirm && (
        <DeleteConfirmModal
          imageIndex={deleteImageIndex}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  );
};

export default DesktopPostMediaManager;
